import { HaulRateDto } from 'common/api/dto/rates.dto';
import {
  WizardDropdown,
  WizardDropdownOption,
} from 'common/components/WizardControls/WizardDropdown';
import { WizardNumericInput } from 'common/components/WizardControls/WizardNumericInput';
import { Contractor } from 'common/models';
import { HaulRate } from 'common/models/harvestData/hauler';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { createContractorOptions } from 'utils/formValues/dropdownOptions';
import { DataEntryRow } from '../SchedulePickWizard/styles';
import {
  InfoLabel,
  WizardRow,
  WizardSection,
  selectBaseStyling,
} from '../WizardComponents/wizardStyles';
import {
  HaulRateWarning,
  PickDataValues,
  PickDataWizardContext,
} from './PickDataWizard';
import { CrewHeader, FormHeader } from './styles';

export type HaulData = {
  /** String auto-generated by `useForm`'s `useFieldArray` hook */
  id: string;
  hauler: WizardDropdownOption<Contractor> | null;
  binsHauled: string | null;
  tarps: string | null;
  haulRate: WizardDropdownOption<HaulRate> | null;
};

export const HaulDataForm: FC<{
  itemIndex: number;
  itemData: HaulData;
  isLoading: boolean;
  removeBtn: () => JSX.Element;
}> = ({ itemIndex, itemData, isLoading, removeBtn }) => {
  const { contractors, haulRates, binsEntered } = useContext(
    PickDataWizardContext,
  );
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<PickDataValues>();

  const haulerOptions: WizardDropdownOption<Contractor>[] = useMemo(
    () => createContractorOptions(contractors),
    [contractors],
  );

  const haulRateOptions: WizardDropdownOption<HaulRateDto>[] = useMemo(
    () =>
      haulRates
        ?.filter(rate => rate.range.length > 1)
        .map(rate => ({
          label: `${rate.range} mi`,
          value: rate,
        })) || [],
    [haulRates],
  );
  const [selectedHauler, setSelectedHauler] = useState(itemData.hauler);
  const [showWarning, setShowWarning] = useState(false);

  const watchForm = watch(value => {
    const record = value?.haulDataRecords?.[itemIndex];

    if (
      itemData?.haulRate?.value.deletedAt &&
      itemData?.haulRate?.value.id !== record?.haulRate?.value?.id
    ) {
      setShowWarning(true);
    }

    setSelectedHauler(record?.hauler as WizardDropdownOption<Contractor>);
  });

  const createNestedPathError = useCallback(
    (name: string) => {
      // react-hook typing does not account for nested errors
      return (
        errors.haulDataRecords as unknown as Record<
          string,
          Record<string, string>
        >[]
      )?.[itemIndex]?.[`${name}`]?.message;
    },
    [errors.haulDataRecords, itemIndex],
  );

  // Clean up watcher
  useEffect(() => {
    return () => watchForm.unsubscribe();
  }, [watchForm]);

  return (
    <>
      <FormHeader>
        <>
          <WizardRow>
            <CrewHeader>Hauler {itemIndex + 1}:</CrewHeader>
            <CrewHeader>
              {selectedHauler ? selectedHauler.value.code : ''}
            </CrewHeader>
          </WizardRow>
          {removeBtn()}
        </>
      </FormHeader>
      <WizardSection>
        <DataEntryRow>
          <InfoLabel>Contractor</InfoLabel>
          <WizardDropdown<Contractor>
            name={`haulDataRecords[${itemIndex}].hauler`}
            options={haulerOptions}
            formControl={control}
            styles={selectBaseStyling}
            isLoading={isLoading}
            nestedPathError={createNestedPathError('hauler')}
            isDisabled={binsEntered}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Bins Hauled</InfoLabel>
          <WizardNumericInput
            name={`haulDataRecords[${itemIndex}].binsHauled`}
            nestedPathError={createNestedPathError('binsHauled')}
            isDisabled={binsEntered}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Tarps</InfoLabel>
          <WizardNumericInput
            name={`haulDataRecords[${itemIndex}].tarps`}
            nestedPathError={createNestedPathError('tarps')}
            isDisabled={binsEntered}
          />
        </DataEntryRow>
        {showWarning && <DataEntryRow>{HaulRateWarning}</DataEntryRow>}
        <DataEntryRow>
          <InfoLabel>Haul Rate</InfoLabel>
          <WizardDropdown<HaulRateDto>
            name={`haulDataRecords[${itemIndex}].haulRate`}
            options={haulRateOptions}
            formControl={control}
            styles={selectBaseStyling}
            nestedPathError={createNestedPathError('haulRate')}
            isDisabled={binsEntered}
          />
        </DataEntryRow>
      </WizardSection>
    </>
  );
};
